import { format } from "date-fns";
import * as XLSX from 'xlsx';
import axios from "../../services/axios";
import { formatDateReport, formatHourExport, parseUtcDate } from "../date/formatedDate";
import { applyColumnWidths } from "./columnWidthCalculator";
import { translateMonitoringStatus, translateMonitoringTask } from "../../i18n/translateMonitoringTask";

interface ExportTasksMonitoringParams {
    rangeDays?: {
        from?: string | Date;
        to?: string | Date;
    };
    queryToSearch?: string;
    filters?: string;
    selectedOrder?: string;
    sortAsc?: boolean;
}

const transformDataForExport = (data: any): any[] => {
    if (!data || !data.content || !Array.isArray(data.content)) {
        console.warn("Unexpected data format for export");
        return [];
    }


    return data.content.map((item: any) => {
        const isBatch = item.micro_area_initials ? item.micro_area_initials.split("-")[2].includes("LT") : false
        const created = parseUtcDate(item?.created_at);
        const updated = parseUtcDate(item?.updated_at);
        const synced = parseUtcDate(item?.synced_at);

        return {
            'id': item.id,
            'Fazenda': item.farm_name,
            'Retiro': item.area_name,
            'Responsavel': item.name,
            'Pasto': isBatch ? null : item.micro_area_initials,
            'Lote': isBatch ? item.micro_area_initials : null,
            'Tarefa': translateMonitoringTask(item.task),
            'Status': translateMonitoringStatus(item.status),
            'Data de criação': item.created_at ? formatDateReport(created) : '',
            'Horario de criação': item.created_at ? formatHourExport(created) : '',
            'Data de execução': item.updated_at ? formatDateReport(updated) : '',
            'Horario de execução': item.updated_at ? formatHourExport(updated) : '',
            'Data de sincronização': item.synced_at ? formatDateReport(synced) : '',
            'Horario de sincronização': item.synced_at ? formatHourExport(synced) : '',
        };
    });
}

export const exportTasksMonitoringToExcel = async ({
    rangeDays,
    queryToSearch,
    filters,
    selectedOrder,
    sortAsc,
}: ExportTasksMonitoringParams): Promise<void> => {
    try {
        const page = 0;
        const size = 100000;
        const dateBegin = rangeDays?.from
            ? format(new Date(rangeDays.from), "y-MM-dd")
            : undefined;
        const dateEnd = rangeDays?.to
            ? format(new Date(rangeDays.to), "y-MM-dd")
            : undefined;

        let extraParams: Record<string, any> = {};
        try {
            const taskTypeEnumObj = JSON.parse(
                localStorage.getItem("TaskTypeEnumObj") || "{}"
            );
            if (queryToSearch && typeof queryToSearch === "string") {
                const matchedKey = Object.keys(taskTypeEnumObj).find(
                    (key) => taskTypeEnumObj[key] === queryToSearch
                );
                if (matchedKey) {
                    extraParams.task_type = matchedKey;
                } else {
                    extraParams.query = queryToSearch;
                }
            }
        } catch (e) {
            if (queryToSearch) extraParams.query = queryToSearch;
        }

        const params: any = {
            size,
            page,
            ...(filters ? { status: filters } : {}),
            ...extraParams,
            ...(dateBegin ? { date_begin: dateBegin } : {}),
            ...(dateEnd ? { date_end: dateEnd } : {}),
            sort_by: selectedOrder,
            sort_direction: sortAsc ? "asc" : "desc",
        };

        const { data } = await axios.get(`/tasks/monitor`, { params });
        const items = Array.isArray(data.content)
            ? data.content
            : Array.isArray(data)
                ? data
                : [];

        if (!items.length) {
            console.warn("No items to export");
            return;
        }

        const transformedData = transformDataForExport(data);

        // Ordenar por Data (decrescente), Fazenda, Retiro e Lote
        transformedData.sort((a, b) => {
            const dateA = new Date(a.Data).getTime();
            const dateB = new Date(b.Data).getTime();
            if (dateB !== dateA) return dateB - dateA;

            const fazendaA = a.Fazenda || '';
            const fazendaB = b.Fazenda || '';
            if (fazendaA !== fazendaB) return fazendaA.localeCompare(fazendaB);

            const retiroA = a.Retiro || '';
            const retiroB = b.Retiro || '';
            if (retiroA !== retiroB) return retiroA.localeCompare(retiroB);

            const loteA = a.Lote || '';
            const loteB = b.Lote || '';
            return loteA.localeCompare(loteB);
        });

        const ws = XLSX.utils.json_to_sheet(transformedData);
        applyColumnWidths(ws, transformedData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Tasks");
        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `tasks_export_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (err) {
        console.error("Error exporting Excel", err);
        throw err;
    }
};
